import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Terminal, Play, Pause, Trash2, Download, Lock, Unlock } from 'lucide-react';
import api from '../services/api';

const LEVEL_COLORS = {
  error: 'text-red-400',
  warn: 'text-yellow-400',
  info: 'text-green-400',
};

export default function RuntimeLogs() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [paused, setPaused] = useState(false);
  const [locked, setLocked] = useState(false);
  const [filter, setFilter] = useState('');
  const [sinceTs, setSinceTs] = useState(null);
  const logContainerRef = useRef(null);
  const pausedRef = useRef(false);
  const lockedRef = useRef(false);
  const sinceRef = useRef(null);

  pausedRef.current = paused;
  lockedRef.current = locked;
  sinceRef.current = sinceTs;

  useEffect(() => {
    const fetchLogs = async () => {
      if (pausedRef.current) return;
      try {
        const since = sinceRef.current;
        const params = since
          ? { limit: 5000, since }
          : { limit: 5000 };
        const res = await api.get('/api/admin/logs/runtime', { params });
        const data = res.data;
        if (data.success && data.data.length > 0) {
          setLogs(prev => {
            const combined = since ? [...prev, ...data.data] : data.data;
            return combined;
          });
          setSinceTs(data.data[data.data.length - 1].time);
        }
      } catch (e) {
        // 忽略网络错误，继续轮询
      }
    };

    fetchLogs();
    const timer = setInterval(fetchLogs, 2000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line

  // Auto-scroll to the newest line while the view is unlocked.
  useEffect(() => {
    if (!lockedRef.current && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const handleClear = () => {
    setLogs([]);
    setSinceTs(null);
  };

  const handleDownload = () => {
    const lines = logs.map(l => {
      const time = l.time.replace('T', ' ').replace('Z', '').slice(0, 23);
      return `[${time}] [${(l.level || 'info').toUpperCase()}] ${l.message}`;
    });
    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const now = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    a.download = `runtime-logs-${now}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filtered = filter
    ? logs.filter(l => l.message.toLowerCase().includes(filter.toLowerCase()))
    : logs;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-slate-950 text-white p-6">
      {/* 标题栏 */}
      <div className="flex items-center gap-3 mb-6 shrink-0">
        <Terminal size={28} className="text-cyan-400" />
        <h1 className="text-2xl font-bold">{t('nav.runtimeLogs')}</h1>
        <span className="ml-auto text-sm text-slate-400">{t('runtimeLogs.count', { count: filtered.length })}</span>
      </div>

      {/* 控制栏 */}
      <div className="flex items-center gap-3 mb-4 shrink-0 flex-wrap">
        <input
          type="text"
          placeholder={t('runtimeLogs.filterPlaceholder')}
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="flex-1 min-w-[240px] px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
        />
        <button
          onClick={() => setPaused(p => !p)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            paused ? 'bg-green-600 hover:bg-green-500' : 'bg-yellow-600 hover:bg-yellow-500'
          }`}
        >
          {paused ? <Play size={15} /> : <Pause size={15} />}
          {paused ? t('runtimeLogs.resume') : t('runtimeLogs.pause')}
        </button>
        <button
          onClick={() => setLocked(l => !l)}
          title={locked ? t('runtimeLogs.unlockTitle') : t('runtimeLogs.lockTitle')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            locked ? 'bg-blue-600 hover:bg-blue-500' : 'bg-slate-700 hover:bg-slate-600'
          }`}
        >
          {locked ? <Lock size={15} /> : <Unlock size={15} />}
          {locked ? t('runtimeLogs.locked') : t('runtimeLogs.lock')}
        </button>
        <button
          onClick={handleDownload}
          title={t('runtimeLogs.downloadTitle')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-cyan-700 hover:bg-cyan-600 transition-colors"
        >
          <Download size={15} />
          {t('runtimeLogs.download')}
        </button>
        <button
          onClick={handleClear}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-700 hover:bg-slate-600 transition-colors"
        >
          <Trash2 size={15} />
          {t('runtimeLogs.clear')}
        </button>
      </div>

      {/* 日志列表 */}
      <div
        ref={logContainerRef}
        className="flex-1 min-h-0 bg-slate-900 rounded-xl border border-slate-700 font-mono text-xs overflow-auto"
      >
        {filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-500">
            {t('runtimeLogs.empty')}
          </div>
        ) : (
          <div className="p-4 space-y-0.5">
            {filtered.map((log, i) => (
              <div key={i} className="flex gap-3 hover:bg-slate-800 px-2 py-0.5 rounded">
                <span className="text-slate-500 shrink-0 w-52">{log.time.replace('T', ' ').replace('Z', '').slice(0, 23)}</span>
                <span className={`shrink-0 w-12 font-bold uppercase ${LEVEL_COLORS[log.level] || 'text-slate-300'}`}>{log.level}</span>
                <span className="text-slate-200 break-all">{log.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
